import { Footprints, FolderInput, FolderMinus, Loader2, MessageSquare, Share2, Square, Trash2, X } from "lucide-react";
import { api } from "@/lib/api";
import { VoiceNoteRecorder } from "./VoiceNoteRecorder";

const when = (iso) => {
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return `${today ? "Today" : d.toLocaleDateString(undefined, { weekday: "short" })} ${
    d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
};

const pill = "inline-flex items-center gap-1 rounded-full border border-white/10 px-2 py-0.5 text-[11px] transition-colors hover:border-brand/50 hover:text-brand";

// One walk: play it again, read the replies, answer back, file it or bin it.
export const WalkRow = ({ walk, speaker, notes, onReplay, onNotes, onReply, onShare, onFile,
                          onForget, onDropNote }) => {
  const id = `walk-${walk.walk_id}`;

  return (
    <li className="flex flex-wrap items-center gap-2 text-xs text-ink-soft">
      <button
        data-testid={`replay-walk-${walk.walk_id}`}
        onClick={() => onReplay(walk)}
        className="inline-flex min-h-[26px] items-center gap-1.5 rounded-full border border-white/10 px-2.5 text-[11px] transition-colors hover:border-brand/50 hover:text-brand"
      >
        {speaker.loading === id ? <Loader2 className="h-3 w-3 animate-spin" />
          : speaker.playing === id ? <Square className="h-3 w-3 text-brand" />
          : <Footprints className="h-3 w-3 text-brand" />}
        {speaker.playing === id ? "Stop" : "Play again"}
      </button>
      <span className="me-auto">
        {when(walk.created_at)} · {walk.stories} stories · {walk.minutes} min
        {walk.language !== "en" ? ` · ${walk.language}` : ""}
      </span>
      <button
        data-testid={`walk-notes-${walk.walk_id}`}
        onClick={() => onNotes(walk)}
        className={`${pill} ${walk.unread ? "border-brand/60 text-brand" : ""}`}
      >
        <MessageSquare className="h-3 w-3" /> Replies
        {walk.unread > 0 && (
          <span className="rounded-full bg-brand px-1.5 text-[10px] font-bold text-white"
                data-testid={`walk-unread-${walk.walk_id}`}>
            {walk.unread}
          </span>
        )}
      </button>
      <button data-testid={`share-walk-${walk.walk_id}`} onClick={() => onShare(walk)} className={pill}>
        <Share2 className="h-3 w-3" />
        {walk.share_token ? "Copy link" : "Share"}
      </button>
      <button
        data-testid={`file-walk-${walk.walk_id}`}
        onClick={() => onFile(walk, !walk.filed)}
        title={walk.filed ? "Take it out of My Walks" : "Move it into My Walks"}
        className={pill}
      >
        {walk.filed ? <FolderMinus className="h-3 w-3" /> : <FolderInput className="h-3 w-3" />}
        {walk.filed ? "Take out" : "Move to folder"}
      </button>
      <button
        data-testid={`forget-walk-${walk.walk_id}`}
        aria-label="Delete this walk"
        title="Delete this walk"
        onClick={() => onForget(walk)}
        className="rounded-full border border-white/10 p-1 text-ink-soft/70 transition-colors hover:border-destructive/60 hover:text-destructive"
      >
        <Trash2 className="h-3 w-3" />
      </button>

      {notes && (
        <div className="mt-1 w-full ps-6" data-testid={`walk-note-list-${walk.walk_id}`}>
          {notes.length === 0 && (
            <p className="text-[11px]" data-testid={`walk-no-notes-${walk.walk_id}`}>
              Nobody has replied yet — you can still leave a word for whoever opens it.
            </p>
          )}
          <ul className="space-y-1">
            {notes.map((note) => (
              <li key={note.note_id} className="flex items-start gap-2 text-[11px]" dir="auto">
                <span className="font-semibold text-brand">
                  {note.name}{note.mine ? " (you)" : ""}
                </span>
                <span>{note.text}</span>
                <button
                  data-testid={`drop-note-${note.note_id}`}
                  aria-label="Remove this reply"
                  onClick={() => onDropNote(walk, note)}
                  className="text-ink-soft/60 hover:text-ink"
                >
                  <X className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-2">
            <VoiceNoteRecorder
              onSend={(blob) => onReply(walk, blob)}
              testid={`walk-reply-${walk.walk_id}`}
              label="Answer in my voice"
            />
          </div>
        </div>
      )}
    </li>
  );
};

export const dropNote = async (walk, note) =>
  api.delete(`/news/walks/${walk.walk_id}/notes/${note.note_id}`);
